import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Collapse,
  CardHeader,
  CardActions,
  Grid,
  Container
} from '@mui/material';
import {
  LocalParking as ParkingIcon,
  Videocam as CameraIcon,
  ElectricBolt as ElectricityIcon,
  Wifi as WifiIcon,
  Refresh as RefreshIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Warning as WarningIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  People as PeopleIcon,
  Speed as SpeedIcon,
  SignalCellularAlt as SignalIcon,
  Water as WaterIcon,
  DeleteOutline as TrashIcon,
  CleaningServices as CleaningIcon,
  AcUnit as FreezerIcon,
  DoorFront as DoorIcon
} from '@mui/icons-material';
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip as RechartsTooltip, PieChart, Pie, Cell, Legend } from 'recharts';
import { getEntitiesByAppId, getEntityById } from '../../api';

const KPIDashboard = ({ entities, fullApp }) => {
  const [kpiData, setKpiData] = useState({
    parking: {
      totalSpaces: 0,
      availableSpaces: 0,
      occupiedSpaces: 0,
      occupancyRate: 0,
      trend: 'stable',
      totalEntities: 0,
      details: []
    },
    crowdCameras: {
      totalCameras: 0,
      totalCrowds: 0,
      activeAlerts: 0,
      averageCrowdSize: 0,
      totalEntities: 0,
      details: []
    },
    electricity: {
      currentConsumption: 0,
      total24hrConsumption: 0,
      activeDevices: 0,
      totalDevices: 0,
      averageUsage: 0,
      totalEntities: 0,
      details: []
    },
    wifi: {
      totalConnections: 0,
      totalAccessPoints: 0,
      activeAlerts: 0,
      averageSignalStrength: 0,
      totalEntities: 44,
      details: []
    },
    stormWater: {
      totalSensors: 0,
      activeAlerts: 0,
      averageWaterLevel: 0,
      maxWaterLevel: 0,
      totalFlowRate: 0,
      averageFlowRate: 0,
      totalEntities: 0,
      details: []
    },
    wasteBins: {
      totalBins: 0,
      needingPickup: 0,
      percentNeedingPickup: 0,
      recycleBins: 0,
      trashCans: 0,
      totalEntities: 0,
      details: []
    },
    paperTowels: {
      totalDispensers: 0,
      lowOrEmpty: 0,
      percentLow: 0,
      avgRemainingPercent: 0,
      totalEntities: 0,
      details: []
    },
    freezers: {
      totalFreezers: 0,
      avgTempC: 0,
      outOfRange: 0,
      totalEntities: 0,
      details: []
    },
    doors: {
      totalDoors: 0,
      openNow: 0,
      eventsToday: 0,
      totalEntities: 0,
      details: []
    }
  });

  // Store initial parking data for realistic updates
  const [initialParkingData, setInitialParkingData] = useState(null);

  const [chartData, setChartData] = useState({
    parking: [],
    electricity24hr: [],
    electricityCurrent: [],
    wifi: [],
    crowd: [], // Added for crowd monitoring chart
    stormWater: [], // Added for storm water monitoring chart
    wasteBins: [],
    paperTowels: [],
    freezers: [],
    doors: []
  });

  // Store initial chart data to maintain historical points
  const [initialChartData, setInitialChartData] = useState({
    electricity24hr: [],
    electricityCurrent: [],
    wifi: [],
    crowd: [], // Added for crowd monitoring chart
    stormWater: [], // Added for storm water monitoring chart
    wasteBins: [],
    paperTowels: [],
    freezers: [],
    doors: []
  });

  const [expandedCards, setExpandedCards] = useState({
    parking: false,
    crowdCameras: false,
    electricity: false,
    wifi: false,
    stormWater: false,
    wasteBins: false,
    paperTowels: false,
    freezers: false,
    doors: false
  });

  // Toggle which KPI cards render; remove entries to hide temporarily
  const KPI_SHOW = [
    //'parking',
   // 'electricity',
    'wifi',
    'crowdCameras',
    'stormWater',
    'wasteBins',
    'paperTowels',
    'freezers',
    'doors'
  ];

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Generate mock real-time data for charts (last 2 minutes, 5-second intervals)
  const generateChartData = useCallback((type, baseValue, variance = 0.1) => {
    const now = new Date();
    const data = [];
    
    // Generate 23 historical points (5-second intervals for 2 minutes = 24 points total)
    for (let i = 23; i >= 1; i--) {
      const time = new Date(now.getTime() - i * 5 * 1000); // 5-second intervals
      const historicalFactor = 1 + (Math.random() - 0.5) * variance * 0.3; // Smaller variance for historical data
      const value = Math.max(0, baseValue * historicalFactor);
      
      data.push({
        time: time.toLocaleTimeString('en-US', { hour12: false, second: '2-digit' }),
        value: Math.round(value * 100) / 100
      });
    }
    
    // Generate only the latest point with more variance for realism
    const latestTime = new Date(now.getTime());
    const randomFactor = 1 + (Math.random() - 0.5) * variance;
    const latestValue = Math.max(0, baseValue * randomFactor);
    
    data.push({
      time: latestTime.toLocaleTimeString('en-US', { hour12: false, second: '2-digit' }),
      value: Math.round(latestValue * 100) / 100
    });
    
    return data;
  }, []);

  // Generate 24-hour consumption data
  const generate24hrData = useCallback(() => {
    const now = new Date();
    const data = [];
    
    // Generate 23 historical points with gradual changes
    for (let i = 23; i >= 1; i--) {
      const time = new Date(now.getTime() - i * 60 * 60 * 1000); // Hourly intervals
      const historicalFactor = 1 + (Math.random() - 0.5) * 0.15; // Smaller variance for historical data
      const value = Math.max(0, 45 * historicalFactor); // Base consumption around 45 kW
      
      data.push({
        time: time.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
        consumption: Math.round(value * 100) / 100
      });
    }
    
    // Generate only the latest point with more variance for realism
    const latestTime = new Date(now.getTime());
    const randomFactor = 1 + (Math.random() - 0.5) * 0.3;
    const latestValue = Math.max(0, 45 * randomFactor);
    
    data.push({
      time: latestTime.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
      consumption: Math.round(latestValue * 100) / 100
    });
    
    return data;
  }, []);

  // Generate crowd monitoring data
  const generateCrowdData = useCallback((baseValue, variance = 0.2) => {
    const now = new Date();
    const data = [];
    
    // Generate 23 historical points (5-second intervals for 2 minutes = 24 points total)
    for (let i = 23; i >= 1; i--) {
      const time = new Date(now.getTime() - i * 5 * 1000); // 5-second intervals
      const historicalFactor = 1 + (Math.random() - 0.5) * variance * 0.3; // Smaller variance for historical data
      const value = Math.max(0, baseValue * historicalFactor);
      
      data.push({
        time: time.toLocaleTimeString('en-US', { hour12: false, second: '2-digit' }),
        crowdSize: Math.round(value)
      });
    }
    
    // Generate only the latest point with more variance for realism
    const latestTime = new Date(now.getTime());
    const randomFactor = 1 + (Math.random() - 0.5) * variance;
    const latestValue = Math.max(0, baseValue * randomFactor);
    
    data.push({
      time: latestTime.toLocaleTimeString('en-US', { hour12: false, second: '2-digit' }),
      crowdSize: Math.round(latestValue)
    });
    
    return data;
  }, []);

  // Generate storm water monitoring data
  const generateStormWaterData = useCallback((baseValue, variance = 0.15) => {
    const now = new Date();
    const data = [];
    
    // Generate 23 historical points (5-second intervals for 2 minutes = 24 points total)
    for (let i = 23; i >= 1; i--) {
      const time = new Date(now.getTime() - i * 5 * 1000); // 5-second intervals
      const historicalFactor = 1 + (Math.random() - 0.5) * variance * 0.3; // Smaller variance for historical data
      const value = Math.max(0, baseValue * historicalFactor);
      
      data.push({
        time: time.toLocaleTimeString('en-US', { hour12: false, second: '2-digit' }),
        waterLevel: Math.round(value * 100) / 100
      });
    }
    
    // Generate only the latest point with more variance for realism
    const latestTime = new Date(now.getTime());
    const randomFactor = 1 + (Math.random() - 0.5) * variance;
    const latestValue = Math.max(0, baseValue * randomFactor);
    
    data.push({
      time: latestTime.toLocaleTimeString('en-US', { hour12: false, second: '2-digit' }),
      waterLevel: Math.round(latestValue * 100) / 100
    });
    
    return data;
  }, []);

  // Generate mock entity data for demonstration
  const generateMockEntities = useCallback(() => {
    const mockEntities = {
      parking: [
        { name: 'Central Parking Lot', capacity: 150, occupied: 89, available: 61, location: 'Downtown' },
        { name: 'Mall Parking', capacity: 200, occupied: 156, available: 44, location: 'Shopping District' },
        { name: 'Office Complex', capacity: 80, occupied: 45, available: 35, location: 'Business District' }
      ],
      crowdCameras: [
        { name: 'Main Square Camera', crowdCount: 45, alerts: 2, location: 'City Center' },
        { name: 'Shopping Mall Entrance', crowdCount: 78, alerts: 1, location: 'Mall Area' },
        { name: 'Train Station', crowdCount: 120, alerts: 3, location: 'Transport Hub' },
        { name: 'Park Entrance', crowdCount: 23, alerts: 0, location: 'Recreation Area' }
      ],
      electricity: [
        { name: 'Street Lights - Downtown', currentConsumption: 45.2, total24hr: 1080, activeDevices: 120, totalDevices: 150, location: 'Downtown' },
        { name: 'Shopping District Lights', currentConsumption: 38.7, total24hr: 928, activeDevices: 85, totalDevices: 100, location: 'Shopping District' },
        { name: 'Park Lighting System', currentConsumption: 22.1, total24hr: 530, activeDevices: 45, totalDevices: 60, location: 'Recreation Area' }
      ],
      wifi: [
        { name: 'Downtown WiFi Hub', connections: 156, signalStrength: 85, alerts: 0, location: 'Downtown' },
        { name: 'Mall WiFi Network', connections: 234, signalStrength: 92, alerts: 1, location: 'Shopping District' },
        { name: 'Public Library WiFi', connections: 67, signalStrength: 78, alerts: 0, location: 'Cultural District' },
        { name: 'Train Station WiFi', connections: 189, signalStrength: 88, alerts: 2, location: 'Transport Hub' }
      ],
      stormWater: [
        { name: 'Downtown Drainage', waterLevel: 2.3, flowRate: 45.2, alerts: 0, location: 'Downtown' },
        { name: 'Shopping District Sewer', waterLevel: 1.8, flowRate: 38.7, alerts: 1, location: 'Shopping District' },
        { name: 'Industrial Zone Outlet', waterLevel: 3.1, flowRate: 67.4, alerts: 2, location: 'Industrial Zone' },
        { name: 'Residential Area Drain', waterLevel: 1.2, flowRate: 22.1, alerts: 0, location: 'Residential Area' },
        { name: 'Park Storm Drain', waterLevel: 0.8, flowRate: 15.6, alerts: 0, location: 'Recreation Area' }
      ],
      wasteBins: Array.from({ length: 60 }).map((_, i) => ({ name: `Bin ${i+1}`, type: i % 2 === 0 ? 'Trash' : 'Recycle', fill: Math.round(40 + Math.random()*55) })),
      paperTowels: Array.from({ length: 20 }).map((_, i) => ({ name: `Dispenser ${i+1}`, remaining: Math.round(30 + Math.random()*60) })),
      freezers: Array.from({ length: 4 }).map((_, i) => ({ name: `Freezer ${i+1}`, tempC: Math.round((-18 + (Math.random()*6 - 3)) * 10)/10 })),
      doors: Array.from({ length: 15 }).map((_, i) => ({ name: `Door ${i+1}`, open: Math.random() < 0.2 }))
    };

    return mockEntities;
  }, []);

  // Helper: stable pseudo-random number based on string
  const seededRandomFromString = useCallback((str) => {
    if (!str) return Math.random();
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = ((hash << 5) - hash) + str.charCodeAt(i);
      hash |= 0;
    }
    const x = Math.sin(hash) * 10000;
    return x - Math.floor(x);
  }, []);

  // Extract real entities by type/name where available
  const extractOperationalEntities = useCallback(() => {
    const all = Array.isArray(entities) ? entities : [];
    const lc = (s) => (s || '').toLowerCase();
    const byName = (e, tokens) => tokens.some(t => lc(e.name).includes(t));

    const wasteBins = all.filter(e => byName(e, ['trash', 'recycle', 'bin', 'trash can']));
    const paperTowels = all.filter(e => byName(e, ['paper towel', 'dispenser']));
    const freezers = all.filter(e => byName(e, ['freezer', 'cooler']));
    const doors = all.filter(e => byName(e, ['door']));

    return { wasteBins, paperTowels, freezers, doors };
  }, [entities]);

  // Extract value from entity keys
  const extractEntityValue = (entity, keyName, defaultValue = 0) => {
    if (!entity.keys || !Array.isArray(entity.keys)) return defaultValue;
    
    const key = entity.keys.find(k => k.keyName === keyName);
    if (!key) return defaultValue;
    
    const value = parseFloat(key.value);
    return isNaN(value) ? defaultValue : value;
  };

  // Calculate KPIs from entities
  const calculateKPIs = useCallback(async () => {
    try {
      // Use mock data for demonstration
      const mockEntities = generateMockEntities();
      const real = extractOperationalEntities();
      
      // Calculate parking KPIs with realistic updates
      const parkingEntities = mockEntities.parking;
      let totalParkingSpaces = 0;
      let availableParkingSpaces = 0;
      let occupiedParkingSpaces = 0;
      
      // Initialize parking data if not set
      if (!initialParkingData) {
        parkingEntities.forEach(entity => {
          totalParkingSpaces += entity.capacity;
          availableParkingSpaces += entity.available;
          occupiedParkingSpaces += entity.occupied;
        });
        setInitialParkingData({
          totalSpaces: totalParkingSpaces,
          availableSpaces: availableParkingSpaces,
          occupiedSpaces: occupiedParkingSpaces
        });
      } else {
        // Update parking data with realistic changes
        const change = Math.floor(Math.random() * 7) - 3; // Random change between -3 and +3
        const newAvailable = Math.max(0, Math.min(initialParkingData.totalSpaces, initialParkingData.availableSpaces + change));
        const newOccupied = initialParkingData.totalSpaces - newAvailable;
        
        totalParkingSpaces = initialParkingData.totalSpaces;
        availableParkingSpaces = newAvailable;
        occupiedParkingSpaces = newOccupied;
      }

      // Calculate crowd camera KPIs
      const cameraEntities = mockEntities.crowdCameras;
      let totalCrowds = 0;
      let activeAlerts = 0;
      let totalCrowdSize = 0;
      
      cameraEntities.forEach(entity => {
        totalCrowds += entity.crowdCount;
        activeAlerts += entity.alerts;
        totalCrowdSize += entity.crowdCount;
      });

      // Calculate electricity KPIs
      const electricityEntities = mockEntities.electricity;
      let currentConsumption = 0;
      let total24hrConsumption = 0;
      let activeDevices = 0;
      let totalDevices = 0;
      
      electricityEntities.forEach(entity => {
        currentConsumption += entity.currentConsumption;
        total24hrConsumption += entity.total24hr;
        activeDevices += entity.activeDevices;
        totalDevices += entity.totalDevices;
      });

      // Calculate WiFi KPIs
      const wifiEntities = mockEntities.wifi;
      let totalConnections = 0;
      let totalAccessPoints = 40;
      let totalSignalStrength = 0;
      let wifiAlerts = 0;
      
      wifiEntities.forEach(entity => {
        totalConnections += entity.connections;
        totalAccessPoints += 1;
        totalSignalStrength += entity.signalStrength;
        wifiAlerts += entity.alerts;
      });

      // Calculate Storm Water KPIs
      const stormWaterEntities = mockEntities.stormWater;
      let totalWaterLevel = 0;
      let maxWaterLevel = 0;
      let totalFlowRate = 0;
      let stormWaterAlerts = 0;
      
      stormWaterEntities.forEach(entity => {
        totalWaterLevel += entity.waterLevel;
        maxWaterLevel = Math.max(maxWaterLevel, entity.waterLevel);
        totalFlowRate += entity.flowRate;
        stormWaterAlerts += entity.alerts;
      });

      // ===== Operational KPIs (Waste, Paper Towels, Freezers, Doors) =====
      // Prefer real entities if present; fallback to mock
      const binsEntities = (real.wasteBins && real.wasteBins.length > 0) ? real.wasteBins : mockEntities.wasteBins;
      const towelsEntities = (real.paperTowels && real.paperTowels.length > 0) ? real.paperTowels : mockEntities.paperTowels;
      const freezerEntities = (real.freezers && real.freezers.length > 0) ? real.freezers : mockEntities.freezers;
      const doorEntities = (real.doors && real.doors.length > 0) ? real.doors : mockEntities.doors;

      // Waste bins stats
      let needingPickup = 0;
      let recycleBins = 0;
      let trashCans = 0;
      const binDetails = binsEntities.map(e => {
        const id = e._id || e.id || e.name;
        const seed = seededRandomFromString(id + 'bin');
        const isRecycle = (e.type ? e.type : e.name)?.toLowerCase().includes('recycle');
        const fill = e.fill != null ? e.fill : Math.round(50 + (seed - 0.5) * 60);
        const needs = fill >= 80;
        if (needs) needingPickup++;
        if (isRecycle) recycleBins++; else trashCans++;
        return { name: e.name, type: isRecycle ? 'Recycle' : 'Trash', fill };
      });

      // Paper towels stats
      let lowOrEmpty = 0;
      let totalRemaining = 0;
      const towelDetails = towelsEntities.map(e => {
        const id = e._id || e.id || e.name;
        const seed = seededRandomFromString(id + 'towel');
        const remaining = e.remaining != null ? e.remaining : Math.max(0, Math.round(60 + (seed - 0.5) * 70));
        if (remaining <= 25) lowOrEmpty++;
        totalRemaining += remaining;
        return { name: e.name, remaining };
      });

      // Freezer stats
      let totalTemp = 0;
      let outOfRange = 0;
      const freezerDetails = freezerEntities.map(e => {
        const id = e._id || e.id || e.name;
        const seed = seededRandomFromString(id + 'freezer');
        const tempC = e.tempC != null ? e.tempC : Math.round(( -18 + (seed - 0.5) * 6 ) * 10) / 10; // -21 to -15
        const oor = tempC > -15 || tempC < -21;
        if (oor) outOfRange++;
        totalTemp += tempC;
        return { name: e.name, tempC };
      });

      // Door stats
      let openNow = 0;
      const doorDetails = doorEntities.map(e => {
        const id = e._id || e.id || e.name;
        const seed = seededRandomFromString(id + 'door');
        const open = e.open != null ? e.open : seed > 0.8; // ~20% open
        if (open) openNow++;
        return { name: e.name, open };
      });

      const totalBins = binsEntities.length;
      const totalDispensers = towelsEntities.length;
      const totalFreezers = freezerEntities.length;
      const totalDoors = doorEntities.length;

      // ===== Update KPI data =====
      setKpiData({
        parking: {
          totalSpaces: totalParkingSpaces,
          availableSpaces: availableParkingSpaces,
          occupiedSpaces: occupiedParkingSpaces,
          occupancyRate: totalParkingSpaces > 0 ? (occupiedParkingSpaces / totalParkingSpaces * 100) : 0,
          trend: availableParkingSpaces > totalParkingSpaces * 0.3 ? 'stable' : 'high',
          totalEntities: parkingEntities.length,
          details: parkingEntities
        },
        crowdCameras: {
          totalCameras: cameraEntities.length,
          totalCrowds: totalCrowds,
          activeAlerts: activeAlerts,
          averageCrowdSize: cameraEntities.length > 0 ? Math.round(totalCrowdSize / cameraEntities.length) : 0,
          totalEntities: cameraEntities.length,
          details: cameraEntities
        },
        electricity: {
          currentConsumption: Math.round(currentConsumption * 100) / 100,
          total24hrConsumption: Math.round(total24hrConsumption * 100) / 100,
          activeDevices: activeDevices,
          totalDevices: totalDevices,
          averageUsage: activeDevices > 0 ? Math.round((currentConsumption / activeDevices) * 100) / 100 : 0,
          totalEntities: electricityEntities.length,
          details: electricityEntities
        },
        wifi: {
          totalConnections: totalConnections,
          totalAccessPoints: totalAccessPoints,
          activeAlerts: wifiAlerts,
          averageSignalStrength: totalAccessPoints > 0 ? Math.round(totalSignalStrength / totalAccessPoints) : 0,
          totalEntities: wifiEntities.length,
          details: wifiEntities
        },
        stormWater: {
          totalSensors: stormWaterEntities.length,
          activeAlerts: stormWaterAlerts,
          averageWaterLevel: stormWaterEntities.length > 0 ? Math.round((totalWaterLevel / stormWaterEntities.length) * 100) / 100 : 0,
          maxWaterLevel: Math.round(maxWaterLevel * 100) / 100,
          totalFlowRate: Math.round(totalFlowRate * 100) / 100,
          averageFlowRate: stormWaterEntities.length > 0 ? Math.round((totalFlowRate / stormWaterEntities.length) * 100) / 100 : 0,
          totalEntities: stormWaterEntities.length,
          details: stormWaterEntities
        },
        wasteBins: {
          totalBins,
          needingPickup,
          percentNeedingPickup: totalBins > 0 ? Math.round((needingPickup / totalBins) * 100) : 0,
          recycleBins,
          trashCans,
          totalEntities: totalBins,
          details: binDetails
        },
        paperTowels: {
          totalDispensers,
          lowOrEmpty,
          percentLow: totalDispensers > 0 ? Math.round((lowOrEmpty / totalDispensers) * 100) : 0,
          avgRemainingPercent: totalDispensers > 0 ? Math.round(totalRemaining / totalDispensers) : 0,
          totalEntities: totalDispensers,
          details: towelDetails
        },
        freezers: {
          totalFreezers,
          avgTempC: totalFreezers > 0 ? Math.round((totalTemp / totalFreezers) * 10) / 10 : 0,
          outOfRange,
          totalEntities: totalFreezers,
          details: freezerDetails
        },
        doors: {
          totalDoors,
          openNow,
          eventsToday: Math.round(totalDoors * 3.2),
          totalEntities: totalDoors,
          details: doorDetails
        }
      });

      // Generate initial chart data if not already set
      if (initialChartData.electricity24hr.length === 0) {
        const initialData = {
          electricity24hr: generate24hrData(),
          electricityCurrent: generateChartData('electricity', currentConsumption, 0.2),
          wifi: generateChartData('wifi', totalConnections, 0.1),
          crowd: generateCrowdData(totalCrowds, 0.2), // Initialize crowd data
          stormWater: generateStormWaterData(totalWaterLevel / stormWaterEntities.length, 0.15), // Initialize storm water data
          wasteBins: generateChartData('waste', Math.max(1, needingPickup), 0.2),
          paperTowels: generateChartData('towels', Math.max(1, lowOrEmpty), 0.2),
          freezers: generateChartData('freezers', Math.max(1, outOfRange), 0.15),
          doors: generateChartData('doors', Math.max(1, openNow), 0.2)
        };
        setInitialChartData(initialData);
        
        // Set initial chart data
        setChartData({
          parking: [
            { name: 'Available', value: availableParkingSpaces, color: '#4caf50' },
            { name: 'Occupied', value: occupiedParkingSpaces, color: '#f44336' }
          ],
          ...initialData
        });
      } else {
        // Update only the latest point for real-time updates
        const updateLatestPoint = (existingData, baseValue, variance = 0.1) => {
          if (existingData.length === 0) return existingData;
          
          // Create a deep copy to avoid mutation
          const updatedData = existingData.map(point => ({ ...point }));
          const latestPoint = updatedData[updatedData.length - 1];
          const randomFactor = 1 + (Math.random() - 0.5) * variance;
          const newValue = Math.max(0, baseValue * randomFactor);
          
          // Update the appropriate field based on data structure
          if (latestPoint.hasOwnProperty('consumption')) {
            latestPoint.consumption = Math.round(newValue * 100) / 100;
          } else if (latestPoint.hasOwnProperty('crowdSize')) {
            latestPoint.crowdSize = Math.round(newValue);
          } else if (latestPoint.hasOwnProperty('waterLevel')) {
            latestPoint.waterLevel = Math.round(newValue * 100) / 100;
          } else {
            latestPoint.value = Math.round(newValue * 100) / 100;
          }
          
          // Update the time to current time
          const now = new Date();
          latestPoint.time = now.toLocaleTimeString('en-US', { hour12: false, second: '2-digit' });
          
          return updatedData;
        };

        // Update chart data
        setChartData({
          parking: [
            { name: 'Available', value: availableParkingSpaces, color: '#4caf50' },
            { name: 'Occupied', value: occupiedParkingSpaces, color: '#f44336' }
          ],
          electricity24hr: updateLatestPoint(initialChartData.electricity24hr, 45, 0.3),
          electricityCurrent: updateLatestPoint(initialChartData.electricityCurrent, currentConsumption, 0.2),
          wifi: updateLatestPoint(initialChartData.wifi, totalConnections, 0.1),
          crowd: updateLatestPoint(initialChartData.crowd, totalCrowds, 0.2), // Update crowd data
          stormWater: updateLatestPoint(initialChartData.stormWater, totalWaterLevel / stormWaterEntities.length, 0.15), // Update storm water data
          wasteBins: updateLatestPoint(initialChartData.wasteBins, Math.max(1, needingPickup), 0.2),
          paperTowels: updateLatestPoint(initialChartData.paperTowels, Math.max(1, lowOrEmpty), 0.2),
          freezers: updateLatestPoint(initialChartData.freezers, Math.max(1, outOfRange), 0.15),
          doors: updateLatestPoint(initialChartData.doors, Math.max(1, openNow), 0.2)
        });
      }

    } catch (error) {
      console.error('Error calculating KPIs:', error);
      setError('Failed to calculate KPIs');
    }
  }, [generateChartData, generateMockEntities, generate24hrData, generateCrowdData, generateStormWaterData, extractOperationalEntities, entities, seededRandomFromString]);

  // Refresh data every 5 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      calculateKPIs();
    }, 5000);

    return () => clearInterval(interval);
  }, [calculateKPIs]);

  // Initial calculation
  useEffect(() => {
    calculateKPIs();
    setLoading(false);
  }, [calculateKPIs]);

  const handleCardExpand = (cardType) => {
    setExpandedCards(prev => ({
      ...prev,
      [cardType]: !prev[cardType]
    }));
  };

  const getTrendIcon = (trend) => {
    switch (trend) {
      case 'up':
        return <TrendingUpIcon color="success" />;
      case 'down':
        return <TrendingDownIcon color="error" />;
      default:
        return <TrendingUpIcon color="primary" />;
    }
  };

  const getStatusColor = (value, threshold) => {
    if (value >= threshold) return 'error';
    if (value >= threshold * 0.7) return 'warning';
    return 'success';
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box sx={{ 
      position: 'fixed', 
      top: 120, 
      right: 16, 
      zIndex: 1000,
      maxHeight: 'calc(100vh - 116px)',
      overflowY: 'auto',
      width: { xs: 'calc(100vw - 32px)', sm: 320, md: 360 }
    }}>
      <Grid container spacing={2} direction="column">
        {/* Parking KPI Card */}
        {KPI_SHOW.includes('parking') && (<Grid item>
          <Card sx={{ backgroundColor: 'rgba(255, 255, 255, 0.95)', backdropFilter: 'blur(10px)' }}>
            <CardHeader
              avatar={<ParkingIcon color="primary" />}
              title="Parking Availability"
              subheader={`${kpiData.parking.totalEntities} entities`}
              action={
                <IconButton size="small" onClick={() => handleCardExpand('parking')}>
                  {expandedCards.parking ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              }
            />
            <Collapse in={!expandedCards.parking}>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="h4" color="primary">
                    {kpiData.parking.availableSpaces}
                  </Typography>
                  <Box textAlign="right">
                    <Typography variant="body2" color="textSecondary">
                      Available
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      / {kpiData.parking.totalSpaces} Total
                    </Typography>
                  </Box>
                </Box>
                
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <Chip 
                    label={`${Math.round(kpiData.parking.occupancyRate)}% Occupied`}
                    color={getStatusColor(kpiData.parking.occupancyRate, 90)}
                    size="small"
                  />
                  {getTrendIcon(kpiData.parking.trend)}
                </Box>

                {/* Pie Chart */}
                <Box height={140} display="flex" justifyContent="center">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={chartData.parking}
                        cx="50%"
                        cy="47%"
                        innerRadius={30}
                        outerRadius={50}
                        paddingAngle={2}
                        dataKey="value"
                      >
                        {chartData.parking.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Legend 
                        verticalAlign="bottom" 
                        height={36}
                        formatter={(value, entry) => <span style={{ color: entry.color }}>{value}</span>}
                      />
                      <RechartsTooltip formatter={(value, name) => [value, name]} />
                    </PieChart>
                  </ResponsiveContainer>
                </Box>
              </CardContent>
            </Collapse>
            <Collapse in={expandedCards.parking}>
              <CardContent sx={{ pt: 0 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Parking Details:
                </Typography>
                {kpiData.parking.details.map((detail, index) => (
                  <Box key={index} display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography variant="body2">{detail.name}</Typography>
                    <Chip 
                      label={`${detail.available}/${detail.capacity}`}
                      size="small"
                      color={detail.available < detail.capacity * 0.2 ? 'error' : 'success'}
                    />
                  </Box>
                ))}
              </CardContent>
            </Collapse>
          </Card>
        </Grid>)}

        {/* Electricity KPI Card */}
        {KPI_SHOW.includes('electricity') && (<Grid item>
          <Card sx={{ backgroundColor: 'rgba(255, 255, 255, 0.95)', backdropFilter: 'blur(10px)' }}>
            <CardHeader
              avatar={<ElectricityIcon color="primary" />}
              title="Electricity Consumption"
              subheader={`${kpiData.electricity.totalEntities} systems`}
              action={
                <IconButton size="small" onClick={() => handleCardExpand('electricity')}>
                  {expandedCards.electricity ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              }
            />
            <Collapse in={!expandedCards.electricity}>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="h4" color="primary">
                    {kpiData.electricity.currentConsumption} kW
                  </Typography>
                  <Box textAlign="right">
                    <Typography variant="body2" color="textSecondary">
                      Current Usage
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {kpiData.electricity.activeDevices}/{kpiData.electricity.totalDevices} Devices
                    </Typography>
                  </Box>
                </Box>
                
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <Chip 
                    label={`${kpiData.electricity.total24hrConsumption} kW (24h)`}
                    color={getStatusColor(kpiData.electricity.currentConsumption, 100)}
                    size="small"
                  />
                  <SpeedIcon color="action" />
                </Box>

                {/* 24hr Consumption Chart */}
                <Box height={100}>
                  {chartData.electricity24hr && chartData.electricity24hr.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%" key="electricity24hr-container">
                      <LineChart data={chartData.electricity24hr} key="electricity24hr" margin={{ top: 5, right: 10, left: 10, bottom: 20 }}>
                        <XAxis 
                          dataKey="time" 
                          tick={{ fontSize: 8 }}
                          interval="preserveStartEnd"
                          angle={-45}
                          textAnchor="end"
                          height={40}
                          width={60}
                          tickMargin={8}
                        />
                        <YAxis 
                          tick={{ fontSize: 10 }}
                          label={{ value: 'kW', angle: -90, position: 'insideLeft', fontSize: 10 }}
                          domain={['dataMin - 5', 'dataMax + 5']}
                          tickFormatter={(value) => Math.round(value)}
                          width={40}
                        />
                        <RechartsTooltip 
                          formatter={(value, name) => [`${value} kW`, name]}
                          labelFormatter={(label) => `Time: ${label}`}
                        />
                        <Line 
                          type="monotone" 
                          dataKey="consumption" 
                          stroke="#4caf50" 
                          strokeWidth={2}
                          dot={{ r: 1.5, fill: "#4caf50" }}
                          name="Consumption"
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  ) : (
                    <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                      <Typography variant="body2" color="textSecondary">
                        Loading chart data...
                      </Typography>
                    </Box>
                  )}
                </Box>
              </CardContent>
            </Collapse>
            <Collapse in={expandedCards.electricity}>
              <CardContent sx={{ pt: 0 }}>
                <Typography variant="subtitle2" gutterBottom>
                  System Details:
                </Typography>
                {kpiData.electricity.details.map((detail, index) => (
                  <Box key={index} display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography variant="body2">{detail.name}</Typography>
                    <Box display="flex" gap={1}>
                      <Chip label={`${detail.currentConsumption} kW`} size="small" />
                      <Chip label={`${detail.activeDevices}/${detail.totalDevices}`} size="small" />
                    </Box>
                  </Box>
                ))}
              </CardContent>
            </Collapse>
          </Card>
        </Grid>)}

        {/* WiFi KPI Card */}
        {KPI_SHOW.includes('wifi') && (<Grid item>
          <Card sx={{ backgroundColor: 'rgba(255, 255, 255, 0.95)', backdropFilter: 'blur(10px)' }}>
            <CardHeader
              avatar={<WifiIcon color="primary" />}
              title="WiFi Network Health"
              subheader={`${kpiData.wifi.totalAccessPoints} access points`}
              action={
                <IconButton size="small" onClick={() => handleCardExpand('wifi')}>
                  {expandedCards.wifi ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              }
            />
            <Collapse in={!expandedCards.wifi}>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="h4" color="primary">
                    {kpiData.wifi.totalConnections}
                  </Typography>
                  <Box textAlign="right">
                    <Typography variant="body2" color="textSecondary">
                      Total Connections
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {kpiData.wifi.averageSignalStrength}% Signal
                    </Typography>
                  </Box>
                </Box>
                
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <Chip 
                    label={`${kpiData.wifi.activeAlerts} Alerts`}
                    color={kpiData.wifi.activeAlerts > 0 ? 'error' : 'success'}
                    size="small"
                    icon={kpiData.wifi.activeAlerts > 0 ? <WarningIcon /> : null}
                  />
                  <SignalIcon color="action" />
                </Box>

                {/* Connection Chart */}
                <Box height={100}>
                  {chartData.wifi && chartData.wifi.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%" key="wifi-container">
                      <LineChart data={chartData.wifi} key="wifi" margin={{ top: 5, right: 10, left: 10, bottom: 20 }}>
                        <XAxis 
                          dataKey="time" 
                          tick={{ fontSize: 8 }}
                          interval="preserveStartEnd"
                          angle={-45}
                          textAnchor="end"
                          height={40}
                          width={60}
                          tickMargin={8}
                        />
                        <YAxis 
                          tick={{ fontSize: 10 }}
                          label={{ value: 'Connections', angle: -90, position: 'insideLeft', fontSize: 10 }}
                          domain={['dataMin - 10', 'dataMax + 10']}
                          tickFormatter={(value) => Math.round(value)}
                          width={40}
                        />
                        <RechartsTooltip 
                          formatter={(value, name) => [`${value} connections`, name]}
                          labelFormatter={(label) => `Time: ${label}`}
                        />
                        <Line 
                          type="monotone" 
                          dataKey="value" 
                          stroke="#9c27b0" 
                          strokeWidth={2}
                          dot={{ r: 1.5, fill: "#9c27b0" }}
                          name="Connections"
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  ) : (
                    <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                      <Typography variant="body2" color="textSecondary">
                        Loading chart data...
                      </Typography>
                    </Box>
                  )}
                </Box>
              </CardContent>
            </Collapse>
            <Collapse in={expandedCards.wifi}>
              <CardContent sx={{ pt: 0 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Network Details:
                </Typography>
                {kpiData.wifi.details.map((detail, index) => (
                  <Box key={index} display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography variant="body2">{detail.name}</Typography>
                    <Box display="flex" gap={1}>
                      <Chip label={`${detail.connections} connections`} size="small" />
                      {detail.alerts > 0 && (
                        <Chip label={`${detail.alerts} alerts`} size="small" color="error" />
                      )}
                    </Box>
                  </Box>
                ))}
              </CardContent>
            </Collapse>
          </Card>
        </Grid>)}

        {/* Crowd Cameras KPI Card */}
        {KPI_SHOW.includes('crowdCameras') && (<Grid item>
          <Card sx={{ backgroundColor: 'rgba(255, 255, 255, 0.95)', backdropFilter: 'blur(10px)' }}>
            <CardHeader
              avatar={<CameraIcon color="primary" />}
              title="Crowd Monitoring"
              subheader={`${kpiData.crowdCameras.totalEntities} cameras`}
              action={
                <IconButton size="small" onClick={() => handleCardExpand('crowdCameras')}>
                  {expandedCards.crowdCameras ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              }
            />
            <Collapse in={!expandedCards.crowdCameras}>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="h4" color="primary">
                    {kpiData.crowdCameras.totalCrowds}
                  </Typography>
                  <Box textAlign="right">
                    <Typography variant="body2" color="textSecondary">
                      Total Crowds
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Avg: {kpiData.crowdCameras.averageCrowdSize}
                    </Typography>
                  </Box>
                </Box>
                
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <Chip 
                    label={`${kpiData.crowdCameras.activeAlerts} Alerts`}
                    color={kpiData.crowdCameras.activeAlerts > 0 ? 'error' : 'success'}
                    size="small"
                    icon={kpiData.crowdCameras.activeAlerts > 0 ? <WarningIcon /> : null}
                />
                <PeopleIcon color="action" />
              </Box>

              {/* Crowd Chart */}
              <Box height={100}>
                {chartData.crowd && chartData.crowd.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%" key="crowd-container">
                    <LineChart data={chartData.crowd} key="crowd" margin={{ top: 5, right: 10, left: 10, bottom: 20 }}>
                      <XAxis 
                        dataKey="time" 
                        tick={{ fontSize: 8 }}
                        interval="preserveStartEnd"
                        angle={-45}
                        textAnchor="end"
                        height={40}
                        width={60}
                        tickMargin={8}
                      />
                      <YAxis 
                        tick={{ fontSize: 10 }}
                        label={{ value: 'People', angle: -90, position: 'insideLeft', fontSize: 10 }}
                        domain={['dataMin - 5', 'dataMax + 5']}
                        tickFormatter={(value) => Math.round(value)}
                        width={40}
                      />
                      <RechartsTooltip 
                        formatter={(value, name) => [`${value} people`, name]}
                        labelFormatter={(label) => `Time: ${label}`}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="crowdSize" 
                        stroke="#ff9800" 
                        strokeWidth={2}
                        dot={{ r: 1.5, fill: "#ff9800" }}
                        name="Crowd Size"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                    <Typography variant="body2" color="textSecondary">
                      Loading chart data...
                    </Typography>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Collapse>
          <Collapse in={expandedCards.crowdCameras}>
            <CardContent sx={{ pt: 0 }}>
              <Typography variant="subtitle2" gutterBottom>
                Camera Details:
              </Typography>
              {kpiData.crowdCameras.details.map((detail, index) => (
                <Box key={index} display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography variant="body2">{detail.name}</Typography>
                  <Box display="flex" gap={1}>
                    <Chip label={`${detail.crowdCount} people`} size="small" />
                    {detail.alerts > 0 && (
                      <Chip label={`${detail.alerts} alerts`} size="small" color="error" />
                    )}
                  </Box>
                </Box>
              ))}
            </CardContent>
          </Collapse>
          </Card>
        </Grid>)}

        {/* Storm Water Monitor KPI Card */}
        {KPI_SHOW.includes('stormWater') && (<Grid item>
          <Card sx={{ backgroundColor: 'rgba(255, 255, 255, 0.95)', backdropFilter: 'blur(10px)' }}>
            <CardHeader
              avatar={<WaterIcon color="primary" />}
              title="Storm Water Monitor"
              subheader={`${kpiData.stormWater.totalEntities} sensors`}
              action={
                <IconButton size="small" onClick={() => handleCardExpand('stormWater')}>
                  {expandedCards.stormWater ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              }
            />
            <Collapse in={!expandedCards.stormWater}>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="h4" color="primary">
                    {kpiData.stormWater.averageWaterLevel}m
                  </Typography>
                  <Box textAlign="right">
                    <Typography variant="body2" color="textSecondary">
                      Avg Water Level
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Max: {kpiData.stormWater.maxWaterLevel}m
                    </Typography>
                  </Box>
                </Box>
                
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <Chip 
                    label={`${kpiData.stormWater.activeAlerts} Alerts`}
                    color={kpiData.stormWater.activeAlerts > 0 ? 'error' : 'success'}
                    size="small"
                    icon={kpiData.stormWater.activeAlerts > 0 ? <WarningIcon /> : null}
                  />
                  <Chip 
                    label={`${kpiData.stormWater.averageFlowRate} L/s`}
                    color="primary"
                    size="small"
                  />
                </Box>

                {/* Water Level Chart */}
                <Box height={100}>
                  {chartData.stormWater && chartData.stormWater.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%" key="stormWater-container">
                      <LineChart data={chartData.stormWater} key="stormWater" margin={{ top: 5, right: 10, left: 10, bottom: 20 }}>
                        <XAxis 
                          dataKey="time" 
                          tick={{ fontSize: 8 }}
                          interval="preserveStartEnd"
                          angle={-45}
                          textAnchor="end"
                          height={40}
                          width={60}
                          tickMargin={8}
                        />
                        <YAxis 
                          tick={{ fontSize: 10 }}
                          label={{ value: 'm', angle: -90, position: 'insideLeft', fontSize: 10 }}
                          domain={['dataMin - 0.2', 'dataMax + 0.2']}
                          tickFormatter={(value) => Math.round(value * 10) / 10}
                          width={40}
                        />
                        <RechartsTooltip 
                          formatter={(value, name) => [`${value} m`, name]}
                          labelFormatter={(label) => `Time: ${label}`}
                        />
                        <Line 
                          type="monotone" 
                          dataKey="waterLevel" 
                          stroke="#2196f3" 
                          strokeWidth={2}
                          dot={{ r: 1.5, fill: "#2196f3" }}
                          name="Water Level"
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  ) : (
                    <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                      <Typography variant="body2" color="textSecondary">
                        Loading chart data...
                      </Typography>
                    </Box>
                  )}
                </Box>
              </CardContent>
            </Collapse>
            <Collapse in={expandedCards.stormWater}>
              <CardContent sx={{ pt: 0 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Sensor Details:
                </Typography>
                {kpiData.stormWater.details.map((detail, index) => (
                  <Box key={index} display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography variant="body2">{detail.name}</Typography>
                    <Box display="flex" gap={1}>
                      <Chip label={`${detail.waterLevel}m`} size="small" />
                      <Chip label={`${detail.flowRate} L/s`} size="small" />
                      {detail.alerts > 0 && (
                        <Chip label={`${detail.alerts} alerts`} size="small" color="error" />
                      )}
                    </Box>
                  </Box>
                ))}
              </CardContent>
            </Collapse>
          </Card>
        </Grid>)}

        {/* Waste Bins KPI Card */}
        {KPI_SHOW.includes('wasteBins') && (<Grid item>
          <Card sx={{ backgroundColor: 'rgba(255, 255, 255, 0.95)', backdropFilter: 'blur(10px)' }}>
            <CardHeader
              avatar={<TrashIcon color="primary" />}
              title="Waste Bin Status"
              subheader={`${kpiData.wasteBins.totalEntities} bins`}
              action={
                <IconButton size="small" onClick={() => handleCardExpand('wasteBins')}>
                  {expandedCards.wasteBins ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              }
            />
            <Collapse in={!expandedCards.wasteBins}>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="h4" color="primary">
                    {kpiData.wasteBins.needingPickup}
                  </Typography>
                  <Box textAlign="right">
                    <Typography variant="body2" color="textSecondary">
                      Need Pickup
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {kpiData.wasteBins.percentNeedingPickup}% of total
                    </Typography>
                  </Box>
                </Box>

                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <Chip 
                    label={`${kpiData.wasteBins.recycleBins} Recycle / ${kpiData.wasteBins.trashCans} Trash`}
                    size="small"
                    color="primary"
                  />
                </Box>

                {/* Need Pickup Trend */}
                <Box height={100}>
                  {chartData.wasteBins && chartData.wasteBins.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%" key="wasteBins-container">
                      <LineChart data={chartData.wasteBins} key="wasteBins" margin={{ top: 5, right: 10, left: 10, bottom: 20 }}>
                        <XAxis 
                          dataKey="time" 
                          tick={{ fontSize: 8 }}
                          interval="preserveStartEnd"
                          angle={-45}
                          textAnchor="end"
                          height={40}
                          width={60}
                          tickMargin={8}
                        />
                        <YAxis 
                          tick={{ fontSize: 10 }}
                          label={{ value: 'Bins', angle: -90, position: 'insideLeft', fontSize: 10 }}
                          domain={[0, 'dataMax + 2']}
                          tickFormatter={(value) => Math.round(value)}
                          width={40}
                        />
                        <RechartsTooltip 
                          formatter={(value, name) => [`${value} bins`, name]}
                          labelFormatter={(label) => `Time: ${label}`}
                        />
                        <Line 
                          type="monotone" 
                          dataKey="value" 
                          stroke="#795548" 
                          strokeWidth={2}
                          dot={{ r: 1.5, fill: "#795548" }}
                          name="Need Pickup"
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  ) : (
                    <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                      <Typography variant="body2" color="textSecondary">
                        Loading chart data...
                      </Typography>
                    </Box>
                  )}
                </Box>
              </CardContent>
            </Collapse>
            <Collapse in={expandedCards.wasteBins}>
              <CardContent sx={{ pt: 0 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Bin Details:
                </Typography>
                {kpiData.wasteBins.details.slice(0, 8).map((detail, index) => (
                  <Box key={index} display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography variant="body2">{detail.name}</Typography>
                    <Chip 
                      label={`${detail.fill}% ${detail.type}`}
                      size="small"
                      color={detail.fill >= 80 ? 'error' : detail.fill >= 60 ? 'warning' : 'success'}
                    />
                  </Box>
                ))}
              </CardContent>
            </Collapse>
          </Card>
        </Grid>)}

        {/* Paper Towels KPI Card */}
        {KPI_SHOW.includes('paperTowels') && (<Grid item>
          <Card sx={{ backgroundColor: 'rgba(255, 255, 255, 0.95)', backdropFilter: 'blur(10px)' }}>
            <CardHeader
              avatar={<CleaningIcon color="primary" />}
              title="Paper Towels Service"
              subheader={`${kpiData.paperTowels.totalEntities} dispensers`}
              action={
                <IconButton size="small" onClick={() => handleCardExpand('paperTowels')}>
                  {expandedCards.paperTowels ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              }
            />
            <Collapse in={!expandedCards.paperTowels}>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="h4" color="primary">
                    {kpiData.paperTowels.lowOrEmpty}
                  </Typography>
                  <Box textAlign="right">
                    <Typography variant="body2" color="textSecondary">
                      Low/Empty
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Avg remaining: {kpiData.paperTowels.avgRemainingPercent}%
                    </Typography>
                  </Box>
                </Box>

                <Box height={100}>
                  {chartData.paperTowels && chartData.paperTowels.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%" key="paperTowels-container">
                      <LineChart data={chartData.paperTowels} key="paperTowels" margin={{ top: 5, right: 10, left: 10, bottom: 20 }}>
                        <XAxis dataKey="time" tick={{ fontSize: 8 }} interval="preserveStartEnd" angle={-45} textAnchor="end" height={40} width={60} tickMargin={8} />
                        <YAxis tick={{ fontSize: 10 }} label={{ value: 'Dispensers', angle: -90, position: 'insideLeft', fontSize: 10 }} domain={[0, 'dataMax + 2']} tickFormatter={(value) => Math.round(value)} width={40} />
                        <RechartsTooltip formatter={(value) => [`${value} low/empty`, 'Dispensers']} labelFormatter={(label) => `Time: ${label}`} />
                        <Line type="monotone" dataKey="value" stroke="#00796b" strokeWidth={2} dot={{ r: 1.5, fill: "#00796b" }} name="Low/Empty" />
                      </LineChart>
                    </ResponsiveContainer>
                  ) : (
                    <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                      <Typography variant="body2" color="textSecondary">Loading chart data...</Typography>
                    </Box>
                  )}
                </Box>
              </CardContent>
            </Collapse>
            <Collapse in={expandedCards.paperTowels}>
              <CardContent sx={{ pt: 0 }}>
                <Typography variant="subtitle2" gutterBottom>Dispenser Details:</Typography>
                {kpiData.paperTowels.details.slice(0, 8).map((detail, index) => (
                  <Box key={index} display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography variant="body2">{detail.name}</Typography>
                    <Chip label={`${detail.remaining}%`} size="small" color={detail.remaining <= 10 ? 'error' : detail.remaining <= 25 ? 'warning' : 'success'} />
                  </Box>
                ))}
              </CardContent>
            </Collapse>
          </Card>
        </Grid>)}

        {/* Freezers KPI Card */}
        {KPI_SHOW.includes('freezers') && (<Grid item>
          <Card sx={{ backgroundColor: 'rgba(255, 255, 255, 0.95)', backdropFilter: 'blur(10px)' }}>
            <CardHeader
              avatar={<FreezerIcon color="primary" />}
              title="Freezer Health"
              subheader={`${kpiData.freezers.totalEntities} units`}
              action={
                <IconButton size="small" onClick={() => handleCardExpand('freezers')}>
                  {expandedCards.freezers ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              }
            />
            <Collapse in={!expandedCards.freezers}>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="h4" color="primary">
                    {kpiData.freezers.avgTempC}°C
                  </Typography>
                  <Box textAlign="right">
                    <Typography variant="body2" color="textSecondary">Avg Temperature</Typography>
                    <Typography variant="body2" color="textSecondary">Out of range: {kpiData.freezers.outOfRange}</Typography>
                  </Box>
                </Box>

                <Box height={100}>
                  {chartData.freezers && chartData.freezers.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%" key="freezers-container">
                      <LineChart data={chartData.freezers} key="freezers" margin={{ top: 5, right: 10, left: 10, bottom: 20 }}>
                        <XAxis dataKey="time" tick={{ fontSize: 8 }} interval="preserveStartEnd" angle={-45} textAnchor="end" height={40} width={60} tickMargin={8} />
                        <YAxis tick={{ fontSize: 10 }} label={{ value: 'Temp (°C)', angle: -90, position: 'insideLeft', fontSize: 10 }} domain={[0, 'dataMax + 5']} tickFormatter={(value) => Math.round(value)} width={40} />
                        <RechartsTooltip formatter={(value) => [`${value} value`, 'Metric']} labelFormatter={(label) => `Time: ${label}`} />
                        <Line type="monotone" dataKey="value" stroke="#1565c0" strokeWidth={2} dot={{ r: 1.5, fill: "#1565c0" }} name="Avg Temp Offset" />
                      </LineChart>
                    </ResponsiveContainer>
                  ) : (
                    <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                      <Typography variant="body2" color="textSecondary">Loading chart data...</Typography>
                    </Box>
                  )}
                </Box>
              </CardContent>
            </Collapse>
            <Collapse in={expandedCards.freezers}>
              <CardContent sx={{ pt: 0 }}>
                <Typography variant="subtitle2" gutterBottom>Unit Details:</Typography>
                {kpiData.freezers.details.map((detail, index) => (
                  <Box key={index} display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography variant="body2">{detail.name}</Typography>
                    <Chip label={`${detail.tempC}°C`} size="small" color={(detail.tempC > -15 || detail.tempC < -21) ? 'error' : 'success'} />
                  </Box>
                ))}
              </CardContent>
            </Collapse>
          </Card>
        </Grid>)}

        {/* Door Sensors KPI Card */}
        {KPI_SHOW.includes('doors') && (<Grid item>
          <Card sx={{ backgroundColor: 'rgba(255, 255, 255, 0.95)', backdropFilter: 'blur(10px)' }}>
            <CardHeader
              avatar={<DoorIcon color="primary" />}
              title="Door Sensors"
              subheader={`${kpiData.doors.totalEntities} doors`}
              action={
                <IconButton size="small" onClick={() => handleCardExpand('doors')}>
                  {expandedCards.doors ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              }
            />
            <Collapse in={!expandedCards.doors}>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="h4" color="primary">
                    {kpiData.doors.openNow}
                  </Typography>
                  <Box textAlign="right">
                    <Typography variant="body2" color="textSecondary">Open Now</Typography>
                    <Typography variant="body2" color="textSecondary">Events today: {kpiData.doors.eventsToday}</Typography>
                  </Box>
                </Box>

                <Box height={100}>
                  {chartData.doors && chartData.doors.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%" key="doors-container">
                      <LineChart data={chartData.doors} key="doors" margin={{ top: 5, right: 10, left: 10, bottom: 20 }}>
                        <XAxis dataKey="time" tick={{ fontSize: 8 }} interval="preserveStartEnd" angle={-45} textAnchor="end" height={40} width={60} tickMargin={8} />
                        <YAxis tick={{ fontSize: 10 }} label={{ value: 'Open', angle: -90, position: 'insideLeft', fontSize: 10 }} domain={[0, 'dataMax + 2']} tickFormatter={(value) => Math.round(value)} width={40} />
                        <RechartsTooltip formatter={(value) => [`${value} open`, 'Doors']} labelFormatter={(label) => `Time: ${label}`} />
                        <Line type="monotone" dataKey="value" stroke="#2e7d32" strokeWidth={2} dot={{ r: 1.5, fill: "#2e7d32" }} name="Open Now" />
                      </LineChart>
                    </ResponsiveContainer>
                  ) : (
                    <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                      <Typography variant="body2" color="textSecondary">Loading chart data...</Typography>
                    </Box>
                  )}
                </Box>
              </CardContent>
            </Collapse>
            <Collapse in={expandedCards.doors}>
              <CardContent sx={{ pt: 0 }}>
                <Typography variant="subtitle2" gutterBottom>Door Details:</Typography>
                {kpiData.doors.details.slice(0, 12).map((detail, index) => (
                  <Box key={index} display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography variant="body2">{detail.name}</Typography>
                    <Chip label={detail.open ? 'Open' : 'Closed'} size="small" color={detail.open ? 'warning' : 'success'} />
                  </Box>
                ))}
              </CardContent>
            </Collapse>
          </Card>
        </Grid>)}
      </Grid>
    </Box>
  );
};

export default KPIDashboard; 